/**
 * Connection Service - API Implementation
 * Servicio de conexiones/empalmes de fibra usando la API del servidor
 * @module services/connectionServiceApi
 */

import { apiClient } from './apiClient'

export type ConnectionType = 'FUSION' | 'MECANICO' | 'CONECTOR' | 'SPLITTER'
export type ConnectionEstado = 'ACTIVO' | 'INACTIVO' | 'PENDIENTE' | 'EN_REPARACION'

export interface FiberConnection {
  id: string
  refCode?: string
  tipo: ConnectionType
  cableEntradaId: string
  fibraEntrada: number
  bufferEntrada?: number
  cableSalidaId: string
  fibraSalida: number
  bufferSalida?: number
  nodeId: string
  bandeja?: number
  posicion?: number
  atenuacion?: number
  reflectancia?: number
  estado: ConnectionEstado
  observaciones?: string
  createdAt: string
  updatedAt: string
}

export interface CreateConnectionData {
  tipo: ConnectionType
  cableEntradaId: string
  fibraEntrada: number
  bufferEntrada?: number
  cableSalidaId: string
  fibraSalida: number
  bufferSalida?: number
  nodeId: string
  bandeja?: number
  posicion?: number
  atenuacion?: number
  reflectancia?: number
  observaciones?: string
}

export interface UpdateConnectionData {
  tipo?: ConnectionType
  bandeja?: number | null
  posicion?: number | null
  atenuacion?: number | null
  reflectancia?: number | null
  estado?: ConnectionEstado
  observaciones?: string | null
}

export interface NodeConnectionsResult {
  node: {
    id: string
    refCode?: string
    tipo: string
    nombre: string | null
  }
  totalConnections: number
  byBandeja: Record<number, FiberConnection[]>
  connections: FiberConnection[]
}

export interface FiberPathResult {
  startCable: string
  startFiber: number
  path: Array<{
    connectionId: string
    nodeId: string
    tipo: ConnectionType
    inputCable: string
    inputFiber: number
    outputCable: string
    outputFiber: number
    atenuacion?: number
  }>
  totalSegments: number
  totalAttenuation: number
}

export const connectionServiceApi = {
  /**
   * Obtener todas las conexiones
   */
  async getAll(filters?: { nodeId?: string; cableId?: string; estado?: ConnectionEstado }): Promise<FiberConnection[]> {
    const params = new URLSearchParams()
    if (filters?.nodeId) params.set('nodeId', filters.nodeId)
    if (filters?.cableId) params.set('cableId', filters.cableId)
    if (filters?.estado) params.set('estado', filters.estado)
    
    const query = params.toString()
    return apiClient.get<FiberConnection[]>(`/api/connections${query ? `?${query}` : ''}`)
  },

  /**
   * Obtener conexiones en un nodo específico
   */
  async getByNode(nodeId: string): Promise<NodeConnectionsResult> {
    return apiClient.get<NodeConnectionsResult>(`/api/connections/node/${nodeId}`)
  },

  /**
   * Obtener conexión por ID
   */
  async getById(id: string): Promise<FiberConnection | null> {
    try {
      return await apiClient.get<FiberConnection>(`/api/connections/${id}`)
    } catch (error) {
      return null
    }
  },

  /**
   * Crear nueva conexión
   */
  async create(data: CreateConnectionData): Promise<FiberConnection> {
    return apiClient.post<FiberConnection>('/api/connections', data)
  },

  /**
   * Actualizar conexión
   */
  async update(id: string, data: UpdateConnectionData): Promise<FiberConnection> {
    return apiClient.put<FiberConnection>(`/api/connections/${id}`, data)
  },

  /**
   * Eliminar conexión
   */
  async delete(id: string): Promise<void> {
    return apiClient.delete(`/api/connections/${id}`)
  },

  /**
   * Crear múltiples conexiones (empalme masivo)
   */
  async createBulk(connections: CreateConnectionData[]): Promise<{ message: string; count: number }> {
    return apiClient.post('/api/connections/bulk', { connections })
  },

  /**
   * Trazar camino de una fibra específica
   */
  async traceFiberPath(cableId: string, fiberNumber: number): Promise<FiberPathResult> {
    return apiClient.get<FiberPathResult>(`/api/connections/fiber-path/${cableId}/${fiberNumber}`)
  },
}
